// Gestión de datos del catálogo (solo propietario):
//   - Exportar el catálogo completo a Excel.
//   - Descargar la plantilla e importar masivamente (primera carga) con mapeo
//     de columnas configurable.
//   - Borrar TODO el catálogo (doble confirmación).

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { useAsync } from '../ui/useAsync.js';
import { AlertMessage, AsyncBoundary, Card, Collapsible, Icon, PageHeader } from '../ui/components.js';
import { XLSX_MIME, downloadBase64, downloadUrl } from '../ui/download.js';
import type { ImportMappingConfig, ImportResult } from '../api/endpoints.js';

export function DataManagementView() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('dataMgmt.pageTitle')}
        subtitle={t('dataMgmt.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <ExportSection />
        <ImportSection />
        <DangerSection />
      </div>
    </div>
  );
}

/** Exportar catálogo completo a Excel. */
function ExportSection() {
  const { t } = useTranslation();
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doExport() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.records.exportGet();
      if (res.base64) downloadBase64(res.base64, res.filename, res.content_type ?? XLSX_MIME);
      else if (res.download_url) downloadUrl(res.download_url, res.filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dataMgmt.errExport'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('dataMgmt.exportTitle')} icon="download">
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
        {t('dataMgmt.exportHint')}
      </p>
      <AlertMessage message={error} className="mb-space-sm" />
      <button className="btn-primary w-full" type="button" disabled={busy} onClick={() => void doExport()}>
        <Icon name="download" className="text-[18px]" /> {busy ? t('dataMgmt.generating') : t('dataMgmt.exportToExcel')}
      </button>
    </Card>
  );
}

type ImportPhase = 'idle' | 'uploading' | 'previewing' | 'previewed' | 'importing' | 'done';

/** Importación masiva: plantilla → subir → previsualizar → confirmar. */
function ImportSection() {
  const { t } = useTranslation();
  const api = useApi();
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [s3Key, setS3Key] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [mappingOpen, setMappingOpen] = useState(false);

  async function downloadTemplate() {
    setError(null);
    try {
      const res = await api.catalog.template();
      if (res.base64) downloadBase64(res.base64, res.filename, res.content_type ?? XLSX_MIME);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dataMgmt.errTemplate'));
    }
  }

  function reset() {
    setPhase('idle');
    setS3Key(null);
    setFileName(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setFileName(file.name);
    setPreview(null);
    setResult(null);
    setPhase('uploading');
    try {
      // Sube el .xlsx a S3 con URL prefirmada y luego previsualiza.
      const { upload_url, s3_key } = await api.catalog.importUploadUrl(file.name);
      await fetch(upload_url, {
        method: 'PUT',
        headers: { 'content-type': XLSX_MIME },
        body: file,
      });
      setS3Key(s3_key);
      setPhase('previewing');
      const pv = await api.catalog.importPreview({ s3_key });
      setPreview(pv);
      setPhase('previewed');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dataMgmt.errProcessFile'));
      setPhase('idle');
    }
  }

  async function confirmImport() {
    if (!s3Key) return;
    setPhase('importing');
    setError(null);
    try {
      const res = await api.catalog.import({ s3_key: s3Key });
      setResult(res);
      setPhase('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dataMgmt.errImport'));
      setPhase('previewed');
    }
  }

  return (
    <Card title={t('dataMgmt.importTitle')} icon="upload_file">
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
        {t('dataMgmt.importHint')}
      </p>

      {/* Mapeo de columnas: colapsable, antes de cargar el archivo. */}
      <Collapsible
        title={t('dataMgmt.columnMapping')}
        icon="table_chart"
        open={mappingOpen}
        onToggle={() => setMappingOpen((v) => !v)}
      >
        <MappingSection />
      </Collapsible>

      <div className="flex items-center gap-space-sm mb-space-md">
        <button className="btn-secondary flex-1" type="button" onClick={() => void downloadTemplate()}>
          <Icon name="description" className="text-[18px]" /> {t('dataMgmt.downloadTemplate')}
        </button>
        <button
          className="btn-primary flex-1"
          type="button"
          disabled={phase === 'uploading' || phase === 'previewing' || phase === 'importing'}
          onClick={() => fileRef.current?.click()}
        >
          <Icon name="upload" className="text-[18px]" /> {t('dataMgmt.uploadFile')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => void onFile(e)}
        />
      </div>

      {fileName && (
        <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm truncate">
          <Icon name="attach_file" className="text-[14px]" /> {fileName}
        </p>
      )}

      {(phase === 'uploading' || phase === 'previewing' || phase === 'importing') && (
        <p className="font-body-sm text-body-sm text-primary">
          {phase === 'uploading' && t('dataMgmt.uploading')}
          {phase === 'previewing' && t('dataMgmt.analyzing')}
          {phase === 'importing' && t('dataMgmt.importing')}
        </p>
      )}

      <AlertMessage message={error} className="mb-space-sm" />

      {/* Vista previa antes de confirmar. */}
      {phase === 'previewed' && preview && (
        <div className="flex flex-col gap-space-sm">
          <ImportSummary result={preview} />
          {preview.notEmptyWarning && (
            <div className="p-space-sm rounded-lg bg-goldmine-vg/15 border border-goldmine-vg/40 text-goldmine-vg font-body-sm text-body-sm">
              {t('dataMgmt.notEmptyWarning', { count: preview.existingRecords })}
            </div>
          )}
          <div className="flex items-center gap-space-sm">
            <button className="btn-secondary" type="button" onClick={reset}>{t('dataMgmt.cancel')}</button>
            <button
              className="btn-primary flex-1"
              type="button"
              disabled={preview.valid === 0}
              onClick={() => void confirmImport()}
            >
              <Icon name="check" className="text-[18px]" /> {t('dataMgmt.importN', { count: preview.valid })}
            </button>
          </div>
        </div>
      )}

      {/* Resultado final. */}
      {phase === 'done' && result && (
        <div className="flex flex-col gap-space-sm">
          <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm">
            {t('dataMgmt.importDoneCreated', { count: result.created })}
            {result.skipped > 0 ? t('dataMgmt.importDoneSkipped', { count: result.skipped }) : t('dataMgmt.importDoneDot')}
          </div>
          <ImportSummary result={result} />
          <button className="btn-secondary self-start" type="button" onClick={reset}>
            <Icon name="refresh" className="text-[16px]" /> {t('dataMgmt.importAnother')}
          </button>
        </div>
      )}
    </Card>
  );
}

/** Resumen numérico + primeros issues de una importación/previsualización. */
function ImportSummary({ result }: { result: ImportResult }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-space-xs">
      <div className="grid grid-cols-3 gap-space-xs">
        <Stat label={t('dataMgmt.statRows')} value={result.totalRows} />
        <Stat label={t('dataMgmt.statValid')} value={result.valid} tone="text-goldmine-mint" />
        <Stat label={t('dataMgmt.statErrors')} value={result.skipped} tone={result.skipped > 0 ? 'text-error' : undefined} />
      </div>
      {result.issues.length > 0 && (
        <details className="rounded-lg border border-border-subtle p-space-sm">
          <summary className="font-body-sm text-body-sm text-on-surface-variant cursor-pointer">
            {t('dataMgmt.seeIssues', { count: result.issues.length })}
          </summary>
          <ul className="mt-space-xs flex flex-col gap-1 max-h-48 overflow-auto">
            {result.issues.map((it, i) => (
              <li key={`${it.row}-${i}`} className="font-body-sm text-body-sm text-on-surface-variant">
                {t('dataMgmt.issueRow', { row: it.row, message: it.message })}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Editor del mapeo columna Excel → campo del modelo. */
function MappingSection() {
  const { t } = useTranslation();
  const api = useApi();
  const cfg = useAsync(() => api.config.getImportMapping(), []);
  return (
    <div className="flex flex-col">
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
        {t('dataMgmt.mappingHint')}
      </p>
      <AsyncBoundary state={cfg}>
        {(data) => <MappingEditor initial={data} />}
      </AsyncBoundary>
    </div>
  );
}

function MappingEditor({ initial }: { initial: ImportMappingConfig }) {
  const { t } = useTranslation();
  const api = useApi();
  const [mapping, setMapping] = useState<Record<string, string>>({ ...initial.mapping });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.config.setImportMapping(mapping);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dataMgmt.errSaveMapping'));
    } finally {
      setBusy(false);
    }
  }

  function resetDefaults() {
    setMapping({ ...initial.default });
    setSaved(false);
  }

  return (
    <div className="flex flex-col gap-space-sm">
      <div className="flex flex-col gap-space-xs max-h-96 overflow-auto">
        {initial.fields.map((f) => (
          <div key={f.field} className="flex items-center gap-space-sm">
            <label className="font-body-sm text-body-sm text-on-surface flex-1 min-w-0 truncate">
              {f.label}
              {f.required && <span className="text-error"> *</span>}
            </label>
            <div className="field-slot w-1/2">
              <input
                className="field-input"
                value={mapping[f.field] ?? ''}
                placeholder={initial.default[f.field] ?? ''}
                onChange={(e) => {
                  setSaved(false);
                  setMapping((m) => ({ ...m, [f.field]: e.target.value }));
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <AlertMessage message={error} />
      {saved && <p className="text-goldmine-mint font-body-sm text-body-sm">{t('dataMgmt.mappingSaved')}</p>}
      <div className="flex items-center gap-space-sm">
        <button className="btn-secondary" type="button" onClick={resetDefaults}>{t('dataMgmt.restoreDefaults')}</button>
        <button className="btn-primary flex-1" type="button" disabled={busy} onClick={() => void save()}>
          <Icon name="save" className="text-[18px]" /> {busy ? t('dataMgmt.savingMapping') : t('dataMgmt.saveMapping')}
        </button>
      </div>
    </div>
  );
}

/** Borrado total del catálogo con doble confirmación. */
function DangerSection() {
  const { t } = useTranslation();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ deletedByTable: Record<string, number> } | null>(null);

  // Palabra de confirmación localizada: el input compara contra este mismo valor,
  // así el borrado exige teclear la palabra en el idioma mostrado.
  const CONFIRM_WORD = t('dataMgmt.confirmWord');

  async function wipe() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.catalog.wipe();
      setDone({ deletedByTable: res.deletedByTable });
      setOpen(false);
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dataMgmt.errWipe'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('dataMgmt.dangerTitle')} icon="warning">
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
        {t('dataMgmt.dangerHint')}
      </p>

      {done && (
        <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm mb-space-sm">
          {t('dataMgmt.wipeDone', { count: done.deletedByTable.record ?? 0 })}
        </div>
      )}
      <AlertMessage message={error} className="mb-space-sm" />

      {!open ? (
        <button className="btn-ghost !text-priority-urgent border border-error/40 w-full" type="button" onClick={() => setOpen(true)}>
          <Icon name="delete_forever" className="text-[18px]" /> {t('dataMgmt.wipeAll')}
        </button>
      ) : (
        <div className="flex flex-col gap-space-sm p-space-sm rounded-lg border border-error/40 bg-error-container/10">
          <p className="font-body-sm text-body-sm text-error">
            {t('dataMgmt.wipeConfirmLead', { word: CONFIRM_WORD })}
          </p>
          <div className="field-slot">
            <input
              className="field-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={CONFIRM_WORD}
              autoComplete="off"
            />
          </div>
          <div className="flex items-center gap-space-sm">
            <button className="btn-secondary" type="button" onClick={() => { setOpen(false); setText(''); }}>
              {t('dataMgmt.cancel')}
            </button>
            <button
              className="btn-ghost !text-priority-urgent border border-error/40 flex-1"
              type="button"
              disabled={busy || text.trim() !== CONFIRM_WORD}
              onClick={() => void wipe()}
            >
              <Icon name="delete_forever" className="text-[18px]" /> {busy ? t('dataMgmt.wiping') : t('dataMgmt.confirmWipe')}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, tone = 'text-on-surface' }: { label: string; value: number; tone?: string }) {
  return (
    <div className="surface-rack-card !p-space-sm flex flex-col">
      <span className="section-label text-on-surface-variant">{label}</span>
      <span className={`font-code-catalog text-body-md font-bold ${tone}`}>{value}</span>
    </div>
  );
}
